import { Vec3 } from '@/core/math/Vec3';
import { ReferenceFrameManager } from './ReferenceFrameManager';

export interface OriginShiftEvent {
  /** Amount subtracted from render positions, in metres, in the active frame. */
  delta: Vec3;
  /** The active frame after the shift. */
  frameId: string;
  /** True when the *frame itself* changed rather than only the offset. */
  frameChanged: boolean;
}

export type OriginShiftListener = (event: OriginShiftEvent) => void;

/**
 * Floating-origin system.
 *
 * Logical simulation positions never change. What changes is the offset that
 * is subtracted before handing anything to Three.js:
 *
 *     renderPosition = logicalPosition(activeFrame) - originOffset
 *
 * Two things move the origin:
 *
 *  1. The camera drifting more than `threshold` metres from the current offset.
 *     The offset is snapped to the camera's current position.
 *  2. The active frame changing (leaving a planet's neighbourhood, landing on a
 *     rotating surface). The new offset is chosen so that the *camera's render
 *     position is preserved exactly*, so no visible jump occurs.
 *
 * Listeners are responsible for rebasing whatever they own: meshes, particle
 * systems, audio emitters, cached spatial queries, trails, decals.
 */
export class FloatingOrigin {
  private offset = Vec3.zero();
  private frameId: string;
  private listeners = new Set<OriginShiftListener>();
  private shiftCount = 0;

  constructor(
    private readonly frames: ReferenceFrameManager,
    initialFrameId: string,
    /** Distance from the origin at which a rebase is triggered, in metres. */
    public threshold = 4000,
  ) {
    this.frameId = initialFrameId;
  }

  get activeFrameId(): string {
    return this.frameId;
  }

  /** Current offset in the active frame, in metres. */
  get originOffset(): Vec3 {
    return this.offset.clone();
  }

  get shifts(): number {
    return this.shiftCount;
  }

  onShift(listener: OriginShiftListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Converts an active-frame position into render space. */
  toRender(positionInActiveFrame: Vec3, out = new Vec3()): Vec3 {
    return Vec3.subVectors(positionInActiveFrame, this.offset, out);
  }

  /** Converts a render-space position back into the active frame. */
  toActiveFrame(renderPosition: Vec3, out = new Vec3()): Vec3 {
    return Vec3.addVectors(renderPosition, this.offset, out);
  }

  /**
   * Converts a position given in an arbitrary frame straight to render space.
   * Prefer this over hand-rolling the two-step conversion at call sites.
   */
  renderPositionFrom(position: Vec3, frameId: string, t: number): Vec3 {
    const inActive = this.frames.transformPosition(position, frameId, this.frameId, t);
    return inActive.sub(this.offset);
  }

  /**
   * Re-anchors to a new reference frame without moving anything on screen.
   *
   * The camera's logical position is re-expressed in the new frame and becomes
   * the new offset plus whatever render-space displacement it previously had.
   */
  setActiveFrame(newFrameId: string, cameraPositionInOldFrame: Vec3, t: number): void {
    if (newFrameId === this.frameId) return;
    const cameraRender = Vec3.subVectors(cameraPositionInOldFrame, this.offset);
    const cameraInNew = this.frames.transformPosition(cameraPositionInOldFrame, this.frameId, newFrameId, t);
    const newOffset = Vec3.subVectors(cameraInNew, cameraRender);

    const delta = Vec3.subVectors(newOffset, this.offset);
    this.frameId = newFrameId;
    this.offset = newOffset;
    this.shiftCount++;
    this.notify({ delta, frameId: newFrameId, frameChanged: true });
  }

  /**
   * Call once per frame with the camera's position in the active frame.
   * Returns the applied shift, or null if no rebase was needed.
   */
  update(cameraPositionInActiveFrame: Vec3): Vec3 | null {
    const drift = Vec3.subVectors(cameraPositionInActiveFrame, this.offset);
    if (drift.lengthSq() < this.threshold * this.threshold) return null;
    this.offset.copy(cameraPositionInActiveFrame);
    this.shiftCount++;
    this.notify({ delta: drift, frameId: this.frameId, frameChanged: false });
    return drift;
  }

  /** Forces the offset to a specific value (used when loading a save). */
  reset(frameId: string, offset: Vec3): void {
    const delta = Vec3.subVectors(offset, this.offset);
    const frameChanged = frameId !== this.frameId;
    this.frameId = frameId;
    this.offset.copy(offset);
    this.notify({ delta, frameId, frameChanged });
  }

  private notify(event: OriginShiftEvent): void {
    for (const l of Array.from(this.listeners)) {
      try {
        l(event);
      } catch (err) {
        console.error('[FloatingOrigin] listener threw during origin shift', err);
      }
    }
  }
}
